import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap, switchMap, delay } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnMapResponse,
  ISnMapQueryParams,
  ISnListResponse,
  ISnListQueryParameters,
} from '@lib/api';
import { SnCamera, SnAlarm, SnAlarmType, SnAlarmHeatmap } from '@lib/api/entities';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnAlarmsAPI {
  get isClosingAlarms$(): Observable<boolean> {
    return this._isClosingAlarms$.asObservable();
  }

  private _isClosingAlarms$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getAlarmHeatmap(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnAlarmHeatmap>> {
    return this._httpClient.get('/api/alarms/heatmap/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((alarm) => new SnAlarmHeatmap(alarm)),
      })),
    );
  }

  public closeAlarm(id: string): Observable<SnAlarm> {
    return this._httpClient
      .post(`/api/alarms/${id}/close/`, null)
      .pipe(map((response) => new SnAlarm(response)));
  }

  public getAlarmList(params: ISnListQueryParameters): Observable<ISnListResponse<SnAlarm>> {
    return this._httpClient.get('/api/alarms/list/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((alarm) => new SnAlarm(alarm)),
        aggregations: response.aggregations,
      })),
    );
  }

  public getAlarmTypeList(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnAlarmType>> {
    return this._httpClient.get('/api/alarms/types/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((alarmType: any) => new SnAlarmType(alarmType)),
      })),
    );
  }

  public getAlarm(id: string): Observable<SnAlarm> {
    return this._httpClient
      .get(`/api/alarms/${id}/`)
      .pipe(map((response: Object) => new SnAlarm(response)));
  }

  public getAlarmCameraList(
    id: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnCamera>> {
    return this._httpClient.get(`/api/alarms/${id}/cameras/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((camera) => new SnCamera(camera)),
      })),
    );
  }

  public sendAlarm(data: Object): Observable<SnAlarm> {
    return this._httpClient.post('/api/alarms/', data).pipe(
      map((response: Object) => new SnAlarm(response)),
      tap({
        next: () => this._toastrService.success(t('settings.demoPanel.toasts.alertSentSuccessfully')),
        error: () => this._toastrService.error(t('settings.demoPanel.toasts.alertUnableToSend')),
      }),
    );
  }

  public sendAlarmTo(id: string, data: any): Observable<SnAlarm> {
    return this._httpClient.post(`/api/alarms/${id}/send/`, data).pipe(
      map((response: any) => new SnAlarm(response)),
      tap({
        next: () => this._toastrService.success(t('notifications.alarms.toasts.alertSent')),
        error: () =>
          this._toastrService.error(t('notifications.alarms.toasts.alertSendFailed')),
      }),
    );
  }

  public getAlarmMap(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<SnAlarm>> {
    return this._httpClient.get('/api/maps/live/alarms/', { params }).pipe(
      map((response: any) => {
        return {
          results: response.results.map((alarm) => new SnAlarm(alarm)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public clearAlarms(): void {
    this._dialogService
      .confirm({
        message: 'This will close all alerts, are you sure?',
        confirmButton: {
          text: 'CONFIRM',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          this._isClosingAlarms$.next(true);
          return this._httpClient.post('/api/alarms/close-all/', null);
        }),
        tap({
          next: () => this._toastrService.success(t('notifications.alarms.toasts.closingAll')),
          error: () => {
            this._isClosingAlarms$.next(false);
            this._toastrService.error(t('notifications.alarms.toasts.closeFailed'));
          },
        }),
      )
      .subscribe({
        next: () => {
          this._isClosingAlarms$.next(false);
        },
        error: () => {
          this._isClosingAlarms$.next(false);
        },
      });
  }
}
